import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, Lock, ShieldCheck, Truck, Box, Eye, EyeOff, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";

const SLIDES = [
  "/login-slideshow/01-tmobile.png",
  "/login-slideshow/02-printshop.jpg",
  "/login-slideshow/03-watermelon.jpg",
  "/login-slideshow/04-kitkat.jpg",
  "/login-slideshow/05-cinemark.jpg",
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [sessionExpired] = useState(() => sessionStorage.getItem("dolphin_session_expired") === "1");

  useEffect(() => {
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % SLIDES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      sessionStorage.removeItem("dolphin_session_expired");
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-slate-50">
      {/* Left slideshow panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-10 text-slate-100 lg:flex lg:w-[46%]">
        {/* Slideshow images */}
        <div className="absolute inset-0 z-0" data-testid="login-slideshow">
          {SLIDES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-in-out"
              style={{ opacity: i === slideIndex ? 1 : 0 }}
            />
          ))}
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/65 to-slate-900/85" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(56,189,248,0.18),transparent_55%)]" />
        </div>

        {/* Foreground content - vertically centered */}
        <div className="relative z-10 flex h-full flex-col">
          <div className="flex flex-1 items-center">
            <div className="w-full space-y-6">
              <h1 className="text-4xl font-bold leading-tight tracking-tight drop-shadow-lg">
                Track every DSK,<br />
                <span className="bg-gradient-to-r from-sky-300 to-blue-200 bg-clip-text text-transparent">crate &amp; shipment.</span>
              </h1>
              <div className="grid max-w-md gap-3 pt-2">
                {[
                  { icon: Box, title: "Auto-paired crates", desc: "Every DSK gets a dedicated wooden crate on intake." },
                  { icon: Truck, title: "Full shipment workflow", desc: "Created → Packed → Shipped → Delivered → Installed → Returned." },
                  { icon: ShieldCheck, title: "Role-based access", desc: "Admins, warehouse staff, and clients—each sees what they need." },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3 rounded-xl border border-white/15 bg-slate-950/55 p-4 backdrop-blur-md">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-500/25 text-sky-200">
                      <f.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{f.title}</p>
                      <p className="text-xs text-slate-300">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Slide indicators + copyright */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5" data-testid="slide-indicators">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSlideIndex(i)}
                  aria-label={`Show slide ${i + 1}`}
                  data-testid={`slide-indicator-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === slideIndex ? "w-8 bg-sky-300" : "w-4 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-slate-300/80">Dolphin Digital OOH Media © {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="relative flex flex-1 items-center justify-center bg-slate-50 p-6 lg:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5">
            {/* Logo header — dark strip blended into the card top */}
            <div className="flex items-center justify-center bg-slate-950 px-6 py-5">
              <img src="/dolphin-logo-dark.png" alt="Dolphin Retail Media" className="h-12 w-auto object-contain" />
            </div>
            <div className="p-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Welcome back</h2>
            <p className="mt-1 text-sm text-slate-500">Sign in to manage inventory &amp; shipments</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {sessionExpired && !error && (
                <p data-testid="session-expired-notice" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                  Your session has expired. Please sign in again to continue.
                </p>
              )}
              {error && (
                <p data-testid="login-error" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9" required />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs font-medium text-sky-700 hover:text-sky-900">Forgot password?</Link>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input id="password" data-testid="login-password-input" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-10" required />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-2.5 flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-slate-700 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="login-submit-button" className="w-full h-11 bg-slate-900 hover:bg-slate-800 transition-colors" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
